<template>
	<div
		:class="[
			$style['app-table-row'],
			{
				[$style['app-table-row--header']]: header,
				[$style['app-table-row--clean']]: clean,
				[$style['app-table-row--with-padding']]: withPadding,
				[$style['app-table-row--sticky']]:
					header && (sticky || baseTableStickyHeader),
				[$style['app-table-row--compact']]: compact,
				[$style['app-table-row--dropdown-first']]: dropdownBeforeCheckbox && indentSpace,
				[$style['app-table-row--checkbox-in-dropdown-column']]: checkboxInDropdownColumn && dropdownBeforeCheckbox && indentSpace,
				[$style['app-table-row--has-actions']]: showActions,
				[$style['app-table-row--header-actions-visible']]: !clean && header && selected,
				[$style['app-table-row--actions-open']]: isActionsDropdownOpen,
				[$style['app-table-row--status-dropdown-open']]: isStatusDropdownOpen,
				[$style['app-table-row--indented']]: indentPx > 0,
				'is-dropdown-open': isActionsDropdownOpen || isStatusDropdownOpen
			}
		]"
		:style="rowStyle"
	>
		<div
			v-if="!clean"
			:class="[
				$style['app-table-row__checkbox'],
				{
					[$style['app-table-row__checkbox--pinned-edge']]:
						pinnedChromeEdge === 'checkbox'
				}
			]"
			:style="checkboxCellStyle"
		>
			<slot name="checkbox">
				<BaseCheckbox
					v-if="showCheckbox"
					:disabled="disabled"
					:checked="checked"
					:actions="checkboxActions"
					:indeterminate="indeterminate"
					@change="handleCheckboxChange"
				/>
			</slot>
		</div>
		<transition
			:enter-active-class="$style['app-table-row__header-actions--fade']"
			:leave-active-class="$style['app-table-row__header-actions--fade']"
			:enter-class="$style['app-table-row__header-actions--hidden']"
			:leave-to-class="$style['app-table-row__header-actions--hidden']"
		>
			<div
				v-if="!clean && header && selected"
				:class="$style['app-table-row__header-actions']"
				:style="{ left: headerActionsLeft }"
			>
				<div
					:class="$style['app-table-row__header-actions-bar']"
					:style="{ width: `calc(var(--table-viewport-width, 100%) - ${headerActionsLeft})` }"
				>
					<div :class="$style['app-table-row__header-selected']">
						{{ $t("common.messages.selected-records") }}:
						<strong>{{ selected }}</strong>
					</div>
					<div :class="$style['app-table-row__header-slot']">
						<slot name="header-actions" />
					</div>
				</div>
			</div>
		</transition>
		<div
			v-if="!clean && indentSpace"
			:class="[
				$style['app-table-row-dropdown'],
				{
					[$style['app-table-row-dropdown--pinned-edge']]:
						pinnedChromeEdge === 'dropdown'
				}
			]"
			:style="dropdownCellStyle"
		>
			<BaseIconButton
				v-if="showDropdown"
				:class="[
					$style['app-table-row__caret'],
					{
						[$style['app-table-row__caret--collapsed']]: !isExpanded
					}
				]"
				@click="handleCarretClick"
			>
				<FontAwesomeIcon :icon="['fas', 'caret-down']" />
			</BaseIconButton>
		</div>
		<Draggable
			v-if="header"
			ref="draggableColumns"
			v-model="draggableColumns"
			:move="onMoveCallback"
			handle=".columnHandle"
			group="columns"
			:animation="100"
			:force-fallback="true"
			:fallback-tolerance="5"
			:class="[
				$style['app-table-row-slot'],
				{
					[$style['app-table-row-slot-start']]: align === 'start',
					[$style['app-table-row-slot-center']]: align === 'center',
					[$style['app-table-row-slot-end']]: align === 'end',
					[$style['app-table-row-slot-stretch']]:
						align === 'stretch'
				}
			]"
			@click.native="handleClick"
		>
			<template #header>
				<slot name="header" />
			</template>
			<slot />
		</Draggable>
		<div
			v-else
			ref="columnsWrapper"
			:class="[
				$style['app-table-row-slot'],
				{
					[$style['app-table-row-slot-start']]: align === 'start',
					[$style['app-table-row-slot-center']]: align === 'center',
					[$style['app-table-row-slot-end']]: align === 'end',
					[$style['app-table-row-slot-stretch']]:
						align === 'stretch'
				}
			]"
			@click="handleClick"
		>
			<slot name="header" />
			<slot />
		</div>
		<div
			v-if="showActions"
			:class="$style['app-table-row__actions']"
		>
			<template v-if="actions.length">
				<div :class="$style['app-table-row__actions-anchor']">
					<BaseIconButton
						v-testid="'table-actions'"
						@click.stop="dropdownToggle"
					>
						<FontAwesomeIcon :icon="['fal', 'ellipsis-v']" size="lg" />
					</BaseIconButton>
					<DropdownMenu
						ref="dropdown"
						:class="$style['app-table-row__dropbox']"
						auto-position
						align="right"
						:match-width="false"
						:gap="0"
						@close="isActionsDropdownOpen = false"
					>
						<DropdownMenuList
							v-testid="'edit-table'"
							:list="actions"
							@dropdown-toggle="dropdownToggle"
						/>
					</DropdownMenu>
				</div>
			</template>
			<div
				v-else
				:class="$style['app-table-row__empty-block']"
			/>
		</div>
	</div>
</template>

<script>
import DropdownMenu from '@c:essentials/DropdownMenu'
import DropdownMenuList from '@c:essentials/DropdownMenu/DropdownMenuList'
import Draggable from 'vuedraggable'
import {
	allocateColumnWidths,
	getColumnResizedWidth,
	getColumnSpan,
	resizeColumnSizing
} from '@/utils/tableColumns'

export default {
	name: 'BaseTableRow',
	components: {
		DropdownMenu,
		DropdownMenuList,
		Draggable
	},
	inject: {
		baseTableStickyHeader: {
			default: false
		},
		baseTableWidthCacheStore: {
			default: null
		},
		baseTableRowEventBus: {
			default: null
		}
	},
	provide () {
		return {
			setBaseTableRowStatusDropdownOpen: (isOpen) => {
				this.statusDropdownOpenCount = Math.max(0, this.statusDropdownOpenCount + (isOpen ? 1 : -1))
				this.isStatusDropdownOpen = this.statusDropdownOpenCount > 0
			}
		}
	},
	props: {
		disabled: {
			type: [Boolean, Number],
			default: false
		},
		checkboxActions: {
			type: Array,
			default: () => []
		},
		checkboxIndent: {
			type: Boolean,
			default: false
		},
		selected: {
			type: [String, Number, Boolean],
			default: 0
		},
		align: {
			type: [String],
			default: 'center'
		},
		showDropdown: {
			type: [Boolean, String],
			default: false
		},
		showActions: {
			type: [Boolean, String],
			default: false
		},
		indentSpace: {
			type: Boolean,
			default: true
		},
		indentStep: {
			type: Number,
			default: 20
		},
		columns: {
			type: Array,
			default: () => []
		},
		showCheckbox: {
			type: Boolean,
			default: true
		},
		level: {
			type: Number,
			default: 0
		},
		header: {
			type: [Boolean, String],
			default: false
		},
		clean: {
			type: [Boolean, String],
			default: false
		},
		withPadding: {
			type: [Boolean, String],
			default: true
		},
		compact: {
			type: Boolean,
			default: false
		},
		checked: {
			type: Boolean,
			default: false
		},
		value: {
			type: [String, Number, Boolean],
			default: null
		},
		actions: {
			type: Array,
			default: () => []
		},
		indeterminate: {
			type: Boolean,
			default: false
		},
		sticky: {
			type: Boolean,
			default: false
		},
		dropdownExpanded: {
			type: Boolean,
			default: undefined
		},
		dropdownBeforeCheckbox: {
			type: Boolean,
			default: false
		},
		checkboxInDropdownColumn: {
			type: Boolean,
			default: false
		},
		stickyColumns: {
			type: Number,
			default: 1
		}
	},
	data () {
		return {
			expanded: false,
			columnsInitialized: false,
			localColumns: [],
			detectedColCount: 0,
			columnMinimumWidth: 0,
			columnLayoutWidth: 0,
			rowChromeWidth: 0,
			actionDropdownInstanceId: null,
			isActionsDropdownOpen: false,
			isStatusDropdownOpen: false,
			statusDropdownOpenCount: 0
		}
	},

	computed: {
		headerActionsLeft () {
			const dropdownWidth = this.dropdownBeforeCheckbox && this.indentSpace && !this.checkboxInDropdownColumn
				? 36 + (Math.max(0, this.level) * (this.indentStep || 20))
				: 0
			const checkboxWidth = this.checkboxInDropdownColumn ? 48 : 32
			return `calc(var(--row-indent) + ${dropdownWidth + checkboxWidth}px)`
		},

		tableMinWidth () {
			if (!this.detectedColCount) return null
			return Math.max(this.columnMinimumWidth, this.columnLayoutWidth) + this.rowChromeWidth
		},

		stickyOffsets () {
			if (this.clean) {
				return {
					checkbox: '0px',
					dropdown: '0px',
					firstColumn: '0px',
					firstColumnPx: 0
				}
			}
			const dropdownWidth = this.indentSpace
				? 36 + (Math.max(0, this.level) * (this.indentStep || 20))
				: 0
			if (this.dropdownBeforeCheckbox && dropdownWidth) {
				return {
					dropdown: this.checkboxInDropdownColumn ? `calc(var(--row-indent) + ${dropdownWidth}px)` : '0px',
					checkbox: this.checkboxInDropdownColumn ? '0px' : `calc(var(--row-indent) + ${dropdownWidth}px)`,
					firstColumn: `calc(var(--row-indent) + ${dropdownWidth + 20}px)`,
					firstColumnPx: dropdownWidth + 20
				}
			}
			return {
				checkbox: '0px',
				dropdown: 'calc(var(--row-indent) + 20px)',
				firstColumn: `calc(var(--row-indent) + ${20 + dropdownWidth}px)`,
				firstColumnPx: 20 + dropdownWidth
			}
		},

		pinnedChromeEdge () {
			const pinnedCols = Math.max(
				0,
				Math.min(parseInt(this.stickyColumns, 10) || 0, this.detectedColCount)
			)
			if (pinnedCols > 0) return null
			return this.indentSpace && (!this.dropdownBeforeCheckbox || this.checkboxInDropdownColumn)
				? 'dropdown'
				: 'checkbox'
		},

		checkboxCellStyle () {
			return [
				this.checkboxIndentStyle,
				{ left: this.stickyOffsets.checkbox }
			]
		},

		dropdownCellStyle () {
			return [
				this.dropdownIndentStyle,
				{ left: this.stickyOffsets.dropdown }
			]
		},

		isExpanded () {
			return this.dropdownExpanded !== undefined ? this.dropdownExpanded : this.expanded
		},

		indentPx () {
			if (this.clean || !this.indentSpace || this.level <= 0) return 0
			return this.level * (this.indentStep || 20)
		},

		rowStyle () {
			const style = {}
			if (this.tableMinWidth) style.minWidth = this.tableMinWidth + 'px'
			if (this.indentPx) style['--row-level-indent'] = this.indentPx + 'px'
			return Object.keys(style).length ? style : null
		},

		dropdownIndentStyle () {
			if (this.level <= 0) return null
			const step = this.indentStep || 20
			const indent = this.dropdownBeforeCheckbox ? ' + var(--row-indent)' : ''
			const width = `calc(${36 + (this.level * step)}px${indent})`
			return {
				width,
				minWidth: width,
				paddingLeft: `calc(${(this.level * step)}px${indent})`
			}
		},

		checkboxIndentStyle () {
			if (!this.checkboxIndent || !this.showCheckbox || this.level <= 0) return null
			return {
				transform: `translateX(${(this.level * 40)}px)`
			}
		},

		draggableColumns: {
			get () {
				return this.localColumns
			},
			set (value) {
				const sortedValue = this.sortColumnsFixedFirst(value)
				this.localColumns = sortedValue
				this.handleColumnsChange(sortedValue)
				if (this.header) {
					this.$nextTick(() => {
						if (this._isDestroyed || this._isBeingDestroyed) return
						this.scheduleSetColumns({ rebindOnCountChange: true })
						this.bindResizeListeners()
					})
				}
			}
		}
	},

	watch: {
		stickyOffsets () {
			this.scheduleStickyOffsetUpdate()
		},
		stickyColumns () {
			this.scheduleStickyOffsetUpdate()
		},
		actions (newActions) {
			if (this.showActions && Array.isArray(newActions) && newActions.length) {
				this.subscribeDropdownBus()
			} else {
				this.unsubscribeDropdownBus()
			}
		},
		columns: {
			handler (newColumns) {
				if (!this.header) {
					if (this.columnsInitialized) {
						this.scheduleSetColumns()
					}
					return
				}

				const previousColumns = Array.isArray(this.localColumns)
					? this.localColumns
					: []
				const hasMembershipChange = this.hasColumnsMembershipChange(
					newColumns,
					previousColumns
				)
				const { columns: normalizedColumns, changed: hasSpanScaleAdjustments } =
					this.normalizeColumnsForScale(newColumns, previousColumns, {
						shouldNormalize: hasMembershipChange
					})
				const fixedFirstColumns = this.sortColumnsFixedFirst(normalizedColumns)

				this.localColumns = fixedFirstColumns
				if (hasSpanScaleAdjustments) {
					this.handleColumnsChange(fixedFirstColumns)
				}

				if (this.columnsInitialized) {
					this.$nextTick(() => {
						if (this._isDestroyed || this._isBeingDestroyed) return
						this.scheduleSetColumns({
							rebindOnCountChange: hasMembershipChange
						})
					})
				}

			},
			immediate: true
		}
	},

	mounted () {
		this.$_scheduleStickyOffsets = () => {
			if (this.$_stickyRafId) return
			this.$_stickyRafId = requestAnimationFrame(() => {
				this.$_stickyRafId = null
				if (this._isDestroyed || this._isBeingDestroyed) return
				this.scheduleSetColumns()
				this.updateStickyColumnOffsets()
			})
		}
		if (typeof ResizeObserver !== 'undefined') {
			this.$_stickyResizeObserver = new ResizeObserver(this.$_scheduleStickyOffsets)
			this.$_stickyResizeObserver.observe(this.$el)
		} else {
			window.addEventListener('resize', this.$_scheduleStickyOffsets)
		}
		this.$nextTick(() => {
			if (this.header) {
				this.initializeColumns()
			} else {
				this.setColumns()
				this.columnsInitialized = true
			}
		})
	},

	updated () {
		if (this._isDestroyed || this._isBeingDestroyed) {
			return
		}

		if (!this.columnsInitialized || this.$_resizeActive) return
		if (this.header) return

		this.scheduleSetColumns()
	},

	created () {
		this.actionDropdownInstanceId = Math.random().toString(36).slice(2)
		this.$_dropdownEventBus = this.baseTableRowEventBus || this.$root
		this.$_dropdownHandler = this.handleOtherRowDropdownOpened.bind(this)
		this.$_dropdownBusSubscribed = false
		if (this.showActions && this.actions.length) {
			this.subscribeDropdownBus()
		}
	},

	beforeDestroy () {
		this.unsubscribeDropdownBus()

		if (this.$_stickyResizeObserver) {
			this.$_stickyResizeObserver.disconnect()
			this.$_stickyResizeObserver = null
		}
		if (this.$_scheduleStickyOffsets) {
			window.removeEventListener('resize', this.$_scheduleStickyOffsets)
			this.$_scheduleStickyOffsets = null
		}
		if (this.$_stickyRafId) {
			cancelAnimationFrame(this.$_stickyRafId)
			this.$_stickyRafId = null
		}

		if (this.$_setColumnsRafId) {
			cancelAnimationFrame(this.$_setColumnsRafId)
			this.$_setColumnsRafId = null
		}
		this.$_setColumnsScheduled = false
		this.$_setColumnsShouldRebind = false

		this.columnsInitialized = false
		this.unbindResizeListeners(this.$_boundResizeChildren || [])
		this.$_boundResizeChildren = null
	},

	methods: {
		subscribeDropdownBus () {
			if (this.$_dropdownBusSubscribed || !this.$_dropdownEventBus) return
			this.$_dropdownEventBus.$on('base-table-row:dropdown-opened', this.$_dropdownHandler)
			this.$_dropdownBusSubscribed = true
		},

		unsubscribeDropdownBus () {
			if (!this.$_dropdownBusSubscribed || !this.$_dropdownEventBus) return
			this.$_dropdownEventBus.$off('base-table-row:dropdown-opened', this.$_dropdownHandler)
			this.$_dropdownBusSubscribed = false
		},

		cloneColumns (columns) {
			if (!Array.isArray(columns)) return []

			return columns.map((column) => {
				if (!column || typeof column !== 'object') {
					return column
				}

				return { ...column }
			})
		},

		sortColumnsFixedFirst (columns = []) {
			if (!columns.length) return columns

			const fixed = []
			const rest = []

			columns.forEach((column) => {
				if (column && column.fixed) {
					fixed.push(column)
				} else {
					rest.push(column)
				}
			})

			if (!fixed.length) return columns

			return [...fixed, ...rest]
		},

		getTableCacheStore () {
			if (this.baseTableWidthCacheStore && typeof this.baseTableWidthCacheStore === 'object') {
				return this.baseTableWidthCacheStore
			}

			if (!this.$_localTableCacheStore) {
				this.$_localTableCacheStore = {
					widthBySignature: Object.create(null),
					widthOrder: [],
					normalizedByTransition: Object.create(null),
					normalizedOrder: [],
					maxEntries: 120
				}
			}

			return this.$_localTableCacheStore
		},

		rememberCacheKey (bucketName, orderName, key) {
			const cacheStore = this.getTableCacheStore()
			const order = cacheStore[orderName]
			const bucket = cacheStore[bucketName]

			if (!Array.isArray(order) || !bucket || !key) {
				return
			}

			const existingIndex = order.indexOf(key)
			if (existingIndex !== -1) {
				order.splice(existingIndex, 1)
			}

			order.push(key)

			const maxEntries = cacheStore.maxEntries || 120
			while (order.length > maxEntries) {
				const oldestKey = order.shift()
				if (oldestKey) {
					delete bucket[oldestKey]
				}
			}
		},

		resolveColumnKey (column, index) {
			if (!column || typeof column !== 'object') {
				return `column-${index}`
			}

			return (
				column.sortKey ||
				column.key ||
				column.tag ||
				column.name ||
				`column-${index}`
			)
		},

		buildColumnsMembershipMap (columns = []) {
			const map = Object.create(null)

			for (let index = 0; index < columns.length; index += 1) {
				const key = this.resolveColumnKey(columns[index], index)
				map[key] = (map[key] || 0) + 1
			}

			return map
		},

		hasColumnsMembershipChange (nextColumns = [], previousColumns = []) {
			if (!Array.isArray(nextColumns) || !Array.isArray(previousColumns)) {
				return true
			}

			if (nextColumns.length !== previousColumns.length) {
				return true
			}

			const nextMap = this.buildColumnsMembershipMap(nextColumns)
			const previousMap = this.buildColumnsMembershipMap(previousColumns)
			const nextKeys = Object.keys(nextMap)
			const previousKeys = Object.keys(previousMap)

			if (nextKeys.length !== previousKeys.length) {
				return true
			}

			for (let i = 0; i < nextKeys.length; i += 1) {
				const key = nextKeys[i]
				if (nextMap[key] !== previousMap[key]) {
					return true
				}
			}

			return false
		},

		buildColumnsStateSignature (columns = [], { includeSpan = false } = {}) {
			if (!Array.isArray(columns) || !columns.length) {
				return '0'
			}

			const items = new Array(columns.length)
			for (let index = 0; index < columns.length; index += 1) {
				const key = this.resolveColumnKey(columns[index], index)
				if (!includeSpan) {
					items[index] = key
					continue
				}

				const rawSpan = parseFloat(columns[index] && columns[index].span)
				const span = !isNaN(rawSpan) && rawSpan > 0 ? rawSpan : 1
				items[index] = `${key}:${span}`
			}

			return `${columns.length}|${items.join('|')}`
		},

		buildNormalizationTransitionKey (nextColumns = [], previousColumns = []) {
			const previousSignature = this.buildColumnsStateSignature(previousColumns, {
				includeSpan: true
			})
			const nextSignature = this.buildColumnsStateSignature(nextColumns, {
				includeSpan: true
			})

			return `${previousSignature}=>${nextSignature}`
		},

		normalizeColumnsForScale (nextColumns = [], previousColumns = [], { shouldNormalize = true } = {}) {
			if (!Array.isArray(nextColumns)) {
				return {
					columns: [],
					changed: false
				}
			}

			const normalizedColumns = this.cloneColumns(nextColumns)
			if (
				!shouldNormalize ||
				!Array.isArray(previousColumns) ||
				!previousColumns.length
			) {
				return {
					columns: normalizedColumns,
					changed: false
				}
			}

			const transitionKey = this.buildNormalizationTransitionKey(
				nextColumns,
				previousColumns
			)
			const cacheStore = this.getTableCacheStore()
			const cachedTransition = cacheStore.normalizedByTransition[transitionKey]
			if (cachedTransition) {
				return {
					columns: this.cloneColumns(cachedTransition.columns),
					changed: cachedTransition.changed
				}
			}

			let previousSpanTotal = 0
			let previousSpanCount = 0
			const previousSpansByKey = {}

			previousColumns.forEach((column, index) => {
				const span = parseFloat(column && column.span)
				if (isNaN(span) || span <= 0) return

				const key = this.resolveColumnKey(column, index)
				previousSpansByKey[key] = span
				previousSpanTotal += span
				previousSpanCount += 1
			})

			const previousAverageSpan = previousSpanCount
				? previousSpanTotal / previousSpanCount
				: 1

			if (previousAverageSpan <= 1.5) {
				cacheStore.normalizedByTransition[transitionKey] = {
					columns: this.cloneColumns(normalizedColumns),
					changed: false
				}
				this.rememberCacheKey(
					'normalizedByTransition',
					'normalizedOrder',
					transitionKey
				)

				return {
					columns: normalizedColumns,
					changed: false
				}
			}

			let changed = false
			normalizedColumns.forEach((column, index) => {
				const key = this.resolveColumnKey(column, index)
				if (Object.prototype.hasOwnProperty.call(previousSpansByKey, key)) {
					return
				}

				const span = parseFloat(column && column.span)
				const baseSpan = !isNaN(span) && span > 0 ? span : 1
				if (baseSpan > 1) {
					return
				}

				const scaledSpan = parseFloat((baseSpan * previousAverageSpan).toFixed(3))
				if (isNaN(scaledSpan) || scaledSpan <= 0) {
					return
				}

				column.span = scaledSpan
				changed = true
			})

			cacheStore.normalizedByTransition[transitionKey] = {
				columns: this.cloneColumns(normalizedColumns),
				changed
			}
			this.rememberCacheKey(
				'normalizedByTransition',
				'normalizedOrder',
				transitionKey
			)

			return {
				columns: normalizedColumns,
				changed
			}
		},

		unbindResizeListeners (children = []) {
			children.forEach((child) => {
				if (child.$_resizeColHandler) {
					child.$off('resize-col', child.$_resizeColHandler)
					child.$_resizeColHandler = null
				}
				if (child.$_resizeStartHandler) {
					child.$off('resize-start', child.$_resizeStartHandler)
					child.$_resizeStartHandler = null
				}
			})
		},

		getColumnComponents () {
			const source = this.$refs.draggableColumns || this.$refs.columnsWrapper
			if (!source) return []
			const elements = Array.from((source.$el || source).children)
			return (source.$children || this.$children)
				.filter(child => child.$options.name === 'BaseTableCol')
				.sort((a, b) => elements.indexOf(a.$el) - elements.indexOf(b.$el))
		},

		getColumnWidthContainer () {
			let parent = this.$el?.parentElement
			while (parent) {
				const overflowX = getComputedStyle(parent).overflowX
				if (overflowX === 'auto' || overflowX === 'scroll') {
					return parent
				}
				parent = parent.parentElement
			}
			return this.$el?.parentElement || null
		},

		getColumnLayout (columns = this.getColumnComponents()) {
			if (!columns.length || !this.$el) return null
			const source = this.$refs.draggableColumns || this.$refs.columnsWrapper
			const wrapper = source.$el || source
			const style = getComputedStyle(this.$el)
			const chrome = (parseFloat(style.paddingLeft) || 0) + (parseFloat(style.paddingRight) || 0) +
				Array.from(this.$el.children).filter(el => el !== wrapper && getComputedStyle(el).position !== 'absolute')
					.reduce((sum, el) => sum + el.getBoundingClientRect().width, 0) -
				this.indentPx
			const definitions = columns.map(column => ({
				span: getColumnSpan(column),
				minWidth: column.resolvedMinWidth,
				resizedWidth: column.resolvedResizedWidth
			}))
			const minimumTotal = definitions.reduce((sum, column) => sum + column.minWidth, 0)
			const containerWidth = this.getColumnWidthContainer()?.clientWidth || this.$el.clientWidth
			const availableWidth = Math.max(minimumTotal, containerWidth - chrome)
			const signature = `${availableWidth}|${columns.map((column, index) =>
				`${column.column?.sortKey || column.$vnode.key || index}:${definitions[index].span}:${definitions[index].minWidth}:${definitions[index].resizedWidth || ''}`
			).join('|')}`
			return { columns, definitions, chrome, minimumTotal, availableWidth, signature }
		},

		getColumnsSpanSignature () {
			return this.getColumnLayout()?.signature || null
		},

		scheduleSetColumns ({ rebindOnCountChange = false } = {}) {
			if (!rebindOnCountChange && !this.$_resizeActive) {
				// Skip when the column span signature matches what was last applied:
				// widths are pure functions of spans, so re-running would be redundant.
				const signature = this.getColumnsSpanSignature()
				if (signature && signature === this.$_lastAppliedWidthSignature) {
					return
				}
			}
			if (this.$_setColumnsScheduled) {
				this.$_setColumnsShouldRebind =
					this.$_setColumnsShouldRebind || rebindOnCountChange
				return
			}

			this.$_setColumnsScheduled = true
			this.$_setColumnsShouldRebind = rebindOnCountChange
			this.$_setColumnsRafId = requestAnimationFrame(() => {
				this.$_setColumnsRafId = null
				this.$_setColumnsScheduled = false
				const shouldRebind = this.$_setColumnsShouldRebind
				this.$_setColumnsShouldRebind = false

				if (this._isDestroyed || this._isBeingDestroyed || this.$_resizeActive) {
					return
				}

				this.setColumns()
				if (shouldRebind && this.header) {
					this.$_boundResizeCount = this.detectedColCount
					this.bindResizeListeners()
				}
			})
		},

		handleColumnsChange (newOrder) {
			this.$emit('set-columns', newOrder)
		},

		scheduleStickyOffsetUpdate () {
			this.$nextTick(() => {
				if (this._isDestroyed || this._isBeingDestroyed) return
				this.updateStickyColumnOffsets()
			})
		},

		updateStickyColumnOffsets () {
			const columns = this.getColumnComponents()
			const stickyCount = Math.max(
				0,
				Math.min(parseInt(this.stickyColumns, 10) || 0, columns.length)
			)
			const alignMap = {
				start: 'flex-start',
				center: 'center',
				end: 'flex-end',
				stretch: null
			}
			const alignValue = Object.prototype.hasOwnProperty.call(alignMap, this.align)
				? alignMap[this.align]
				: 'center'
			let measured = 0
			const prefixPx = this.stickyOffsets.firstColumnPx - this.indentPx
			for (let i = 0; i < columns.length; i++) {
				const column = columns[i]
				const stickyLeft = i < stickyCount
					? `calc(var(--row-indent) + ${prefixPx + measured}px)`
					: null
				let stickyAlign = ''
				let stickyAlignAxis = ''
				if (i < stickyCount && column.$el) {
					measured += column.$el.offsetWidth
					if (alignValue) {
						stickyAlign = alignValue
						stickyAlignAxis = getComputedStyle(column.$el)
							.flexDirection.startsWith('column')
							? 'main'
							: 'cross'
					}
				}
				if (column.stickyLeft !== stickyLeft) {
					column.stickyLeft = stickyLeft
				}
				if (column.stickyAlign !== stickyAlign) {
					column.stickyAlign = stickyAlign
				}
				if (column.stickyAlignAxis !== stickyAlignAxis) {
					column.stickyAlignAxis = stickyAlignAxis
				}
				const isLastSticky = i === stickyCount - 1
				if (column.isLastSticky !== isLastSticky) {
					column.isLastSticky = isLastSticky
				}
			}
		},

		onMoveCallback (evt) {
			const draggedEl = evt?.dragged
			const hasHandle = draggedEl?.querySelector('.columnHandle') ||
				draggedEl?.classList?.contains('columnHandle')

			if (
				!this.localColumns ||
				!this.localColumns.length ||
				!hasHandle
			) {
				return false
			}

			const fixedCount = this.localColumns.filter((column) => column && column.fixed).length
			if (!fixedCount) return true

			const draggedIndex = evt?.draggedContext?.index ?? -1
			const relatedIndex = evt?.relatedContext?.index ?? -1
			const willInsertAfter = evt?.willInsertAfter

			if (draggedIndex >= 0 && draggedIndex < fixedCount) {
				return false
			}

			const insertIndex = relatedIndex + (willInsertAfter ? 1 : 0)
			return insertIndex >= fixedCount
		},

		handleColResize (child, targetWidth) {
			const layout = child.$_resizeLayout
			const columnIndex = layout ? layout.columns.indexOf(child) : -1
			const index = child.$_resizeColumnIndex
			if (!layout || targetWidth === null || columnIndex < 0 || !this.localColumns[index]) return
			const sizing = resizeColumnSizing(layout.definitions, columnIndex, targetWidth, layout.availableWidth)
			const resizedWidth = getColumnResizedWidth(this.localColumns[index])
			if (
				sizing.span === getColumnSpan(this.localColumns[index]) &&
				sizing.resizedWidth === resizedWidth
			) return
			const columns = this.cloneColumns(this.localColumns)
			columns[index].span = sizing.span
			if (sizing.resizedWidth === null) {
				delete columns[index].resizedWidth
			} else {
				columns[index].resizedWidth = sizing.resizedWidth
			}
			this.localColumns = columns
			this.handleColumnsChange(columns)
		},

		resolveLocalColumnIndex (child, componentIndex, keyOccurrences = {}) {
			if (child.column) {
				const key = this.resolveColumnKey(child.column, componentIndex)
				const occurrence = keyOccurrences[key] || 0
				keyOccurrences[key] = occurrence + 1
				let seen = 0
				return this.localColumns.findIndex((column, index) => {
					if (this.resolveColumnKey(column, index) !== key) return false
					return seen++ === occurrence
				})
			}
			return this.localColumns.length === this.getColumnComponents().length ? componentIndex : -1
		},

		bindResizeListeners () {
			if (!this.header) return
			this.$nextTick(() => {
				const keyOccurrences = {}
				const colChildren = this.getColumnComponents()
					.map((child, componentIndex) => {
						child.$_resizeColumnIndex = this.resolveLocalColumnIndex(child, componentIndex, keyOccurrences)
						return child
					})
					.filter(child => child.header && child.canResize && child.$_resizeColumnIndex >= 0)

				const hasSameBinding =
					Array.isArray(this.$_boundResizeChildren) &&
					this.$_boundResizeChildren.length === colChildren.length &&
					this.$_boundResizeChildren.every((child, index) => child === colChildren[index])

				if (hasSameBinding) return

				this.unbindResizeListeners(this.$_boundResizeChildren || [])
				colChildren.forEach((child) => {
					child.$_resizeColHandler = (targetWidth) => {
						this.$_resizeActive = false
						this.handleColResize(child, targetWidth)
						child.$_resizeLayout = null
						this.scheduleSetColumns({ rebindOnCountChange: true })
					}
					child.$_resizeStartHandler = () => {
						this.$_resizeActive = true
						child.$_resizeLayout = this.getColumnLayout()
						child.$_resizeMaxWidth = Infinity
					}
					child.$on('resize-col', child.$_resizeColHandler)
					child.$on('resize-start', child.$_resizeStartHandler)
				})
				this.$_boundResizeChildren = colChildren
			})
		},

		initializeColumns () {
			this.$nextTick(() => {
				if (!this.setColumns()) {
					this.scheduleSetColumns()
				}
				this.columnsInitialized = true
				this.bindResizeListeners()
				this.$_boundResizeCount = this.detectedColCount
			})
		},

		setColumns () {
			try {
				return this.calculateColumnWidths(this.getColumnComponents())
			} catch (error) {
				if (process.env.NODE_ENV === 'development') {
					console.error('BaseTableRow: Error in setColumns:', error)
				}
				return false
			}
		},

		calculateColumnWidths (columns) {
			if (!columns || columns.length === 0) {
				return false
			}

			try {
				const layout = this.getColumnLayout(columns)
				if (!layout) return false
				const widthSignature = layout.signature
				this.columnMinimumWidth = layout.minimumTotal
				this.rowChromeWidth = layout.chrome
				const cacheStore = this.getTableCacheStore()
				const cachedWidths = cacheStore.widthBySignature[widthSignature]
				const allocation = allocateColumnWidths(layout.definitions, layout.availableWidth)
				const widths =
					Array.isArray(cachedWidths) && cachedWidths.length === columns.length
						? cachedWidths
						: allocation.widths
				this.columnLayoutWidth = widths.reduce((sum, width) => sum + width, 0)

				if (!cachedWidths || cachedWidths.length !== columns.length) {
					cacheStore.widthBySignature[widthSignature] = widths
					this.rememberCacheKey('widthBySignature', 'widthOrder', widthSignature)
				}

				if (this.$_lastAppliedWidthSignature !== widthSignature) {
					for (let i = 0; i < columns.length; i++) {
						try {
							columns[i].columnWidth = widths[i]
						} catch (childError) {
							if (process.env.NODE_ENV === 'development') {
								console.error(
									'BaseTableRow: Error setting width for child:',
									childError
								)
							}
						}
					}
					this.$_lastAppliedWidthSignature = widthSignature
				}

				if (this.detectedColCount !== columns.length) {
					this.detectedColCount = columns.length
				}

				this.scheduleStickyOffsetUpdate()

				return true
			} catch (error) {
				if (process.env.NODE_ENV === 'development') {
					console.error(
						'BaseTableRow: Error calculating column widths:',
						error
					)
				}
				return false
			}
		},

		dropdownToggle (_e) {
			if (this.$refs.dropdown) {
				const willOpen = !this.$refs.dropdown.isOpen
				this.isActionsDropdownOpen = willOpen
				if (willOpen) {
					this.$_dropdownEventBus.$emit('base-table-row:dropdown-opened', this.actionDropdownInstanceId)
				}
				this.$refs.dropdown.toggle()
			}
		},

		handleCheckboxChange (checked) {
			this.$emit('checkbox', checked, this.value)
		},

		handleClick (e) {
			if (e?.target?.localName !== 'a') {
				this.$emit('click', e)
			}
		},

		handleCarretClick (_e) {
			if (_e?.stopPropagation) {
				_e.stopPropagation()
			}
			if (_e?.preventDefault) {
				_e.preventDefault()
			}

			const nextExpanded = this.dropdownExpanded === undefined
				? !this.expanded
				: !this.dropdownExpanded
			this.$emit('dropdown-click', nextExpanded)
			if (this.dropdownExpanded === undefined) {
				this.expanded = !this.expanded
			}
		},

		handleOtherRowDropdownOpened (id) {
			if (id !== this.actionDropdownInstanceId && this.$refs.dropdown) {
				this.$refs.dropdown.close()
			}
		}
	}
}
</script>

<style lang="scss" module>
.app-table-row {
	position: relative;
	display: flex;
	align-items: center;
	justify-content: space-between;
	background-color: getColor(surface);
	padding: 0 $gutter * 3;
	padding-left: 0;
	font-size: 14px;

	--row-indent: #{$gutter * 3};
	--row-indent-end: #{$gutter * 3};

	border-bottom: 1px solid getColor(border-common);
	transition: background-color 0.12s ease;
	z-index: auto;

	&:not(&--header):hover {
		z-index: 60;

		--row-bg: #{getColor(hover-overlay-solid)};
	}

	@include dark-mode {
		border-bottom-color: getColor(border);

		&:not(&--header):hover {
			background-color: getColor(hover-overlay-solid);
		}
	}

	@include nord-mode {
		&:not(&--header):hover {
			background-color: getColor(hover-overlay-solid);
		}
	}

	&-slot {
		display: flex;
		align-items: center;
		justify-content: space-between;
		flex: 1;
		min-width: 0;

		&--start {
			align-items: flex-start;
		}

		&--center {
			align-items: center;
		}

		&--end {
			align-items: flex-end;
		}

		&--stretch {
			align-items: stretch;
		}
	}

	&--header {
		color: getColor(helper-text);
		border-top-left-radius: 5px;
		border-top-right-radius: 5px;
		z-index: 1;
	}

	&--sticky {
		position: sticky;
		top: 0;
		z-index: 70;
	}

	&--actions-open {
		z-index: 100 !important;
	}

	&--status-dropdown-open {
		z-index: 100 !important;
	}

	&--with-padding {
		padding: 0 $gutter * 3;
		padding-left: 0;

		@include media-breakpoint-only(xs) {
			padding: $gutter;
			padding-left: 0;

			--row-indent: #{$gutter * 2};
			--row-indent-end: #{$gutter};
		}
	}

	&--compact {
		min-height: 32px;
		padding: $gutter;
		padding-left: 0;

		--row-indent: #{$gutter};
		--row-indent-end: #{$gutter};

		.app-table-row-slot {
			min-height: 32px;
		}
	}

	&--clean {
		padding-left: 15px;

		--row-indent: 0px;

		.app-table-row-slot {
			justify-content: center;
		}
	}

	&--has-actions {
		padding-right: 0;
	}

	&__checkbox {
		position: sticky;
		z-index: 3;
		background-color: transparent;
		padding-right: 10px;
		padding-left: var(--row-indent);
		margin-bottom: 0;
		align-self: stretch;
		display: flex;
		align-items: center;
		min-height: 20px;
		width: calc(20px + var(--row-indent));
		flex-shrink: 0;
	}

	&__actions-anchor {
		display: flex;
		align-items: center;
	}

	&__empty-block {
		height: 24px;
		width: 24px;
	}

	&__actions {
		position: sticky;
		right: 0;
		z-index: 1;
		display: flex;
		align-items: center;
		justify-content: center;
		flex-shrink: 0;
		align-self: stretch;
		padding: 0 calc(var(--row-indent-end) / 2);
		background-color: transparent;

		&::before {
			content: '';
			position: absolute;
			top: 0;
			bottom: 0;
			right: 100%;
			width: 6px;
			background: linear-gradient(to left, transparentize(getColor(shadow-light), 0.5), transparent);
			opacity: 0;
			transition: opacity 0.12s ease;
			pointer-events: none;

			@include dark-mode {
				display: none;
			}
		}
	}

	:global(.is-scrolled-x-end) &__actions {
		background-color: var(--row-bg, #{getColor(surface)});

		&::before {
			opacity: var(--sticky-edge-opacity, 1);
		}

		@include dark-mode {
			background-image: var(--sticky-overlay, linear-gradient(rgb(255 255 255 / 0.03), rgb(255 255 255 / 0.03)));
		}
	}

	&__dropbox {
		z-index: 2;
		right: 20px;
		transform-origin: right;
	}

	&-dropdown {
		position: sticky;
		z-index: 1;
		background-color: transparent;
		align-self: stretch;
		width: 36px;
		display: flex;
		align-items: center;
		justify-content: center;
		flex-shrink: 0;
	}

	:global(.is-scrolled-x) &__checkbox,
	:global(.is-scrolled-x) &-dropdown {
		background-color: var(--row-bg, #{getColor(surface)});

		@include dark-mode {
			background-image: var(--sticky-overlay, linear-gradient(rgb(255 255 255 / 0.03), rgb(255 255 255 / 0.03)));
		}
	}

	&--dropdown-first {
		.app-table-row-dropdown {
			order: -1;
			padding-left: var(--row-indent);
			width: calc(36px + var(--row-indent));
		}

		.app-table-row__checkbox {
			padding-left: 0;
			width: 20px;
		}
	}

	&--checkbox-in-dropdown-column {
		.app-table-row__checkbox {
			order: -2;
			justify-content: center;
			padding-left: var(--row-indent);
			padding-right: 0;
			width: calc(36px + var(--row-indent));
		}

		.app-table-row-dropdown {
			padding-left: 0;
			width: 20px;
		}
	}

	&__checkbox--pinned-edge,
	&-dropdown--pinned-edge {
		&::after {
			content: '';
			position: absolute;
			top: 0;
			bottom: 0;
			left: 100%;
			width: 6px;
			background: linear-gradient(to right, transparentize(getColor(shadow-light), 0.5), transparent);
			opacity: 0;
			transition: opacity 0.12s ease;
			pointer-events: none;

			@include dark-mode {
				display: none;
			}
		}
	}

	:global(.is-scrolled-x) &__checkbox--pinned-edge,
	:global(.is-scrolled-x) &-dropdown--pinned-edge {
		&::after {
			opacity: var(--sticky-edge-opacity, 1);
		}
	}

	@include media-breakpoint-only(xs) {
		&__checkbox,
		&-dropdown,
		&__actions,
		:global(.is-scrolled-x) &__checkbox,
		:global(.is-scrolled-x) &-dropdown,
		:global(.is-scrolled-x-end) &__actions {
			position: relative;
			left: auto !important;
			right: auto;
			background-color: transparent;
			background-image: none !important;

			&::before,
			&::after {
				display: none;
			}
		}
	}

	&--header-actions-visible {
		--row-bg: #{getColor(surface)};
		--sticky-overlay: none;
		--sticky-edge-opacity: 0;
	}

	&--indented {
		.app-table-row-dropdown {
			z-index: 2;
		}

		.app-table-row-slot {
			margin-left: calc(-1 * var(--row-level-indent));

			> :first-child::before {
				content: '';
				flex: 0 0 var(--row-level-indent);
			}
		}
	}

	&__caret {
		transition: transform 0.2s ease;

		&--collapsed {
			transform: rotate(-90deg);
		}
	}

	&__header-actions {
		z-index: 2;
		position: sticky;
		flex: 0 0 0;
		width: 0;
		align-self: stretch;

		&--fade {
			transition: opacity 0.12s ease;
		}

		&--hidden {
			opacity: 0;
		}
	}

	&__header-actions-bar {
		position: absolute;
		top: 0;
		bottom: 0;
		left: 0;
		display: flex;
		align-items: center;
		padding-left: 40px;
		background-color: getColor(surface);
		cursor: default;
	}

	&__header-selected {
		color: getColor(text);
		margin-right: 20px;
	}

}

</style>
